import React, { useState } from 'react';
import { connect } from 'react-redux';

import useMartyContext from 'hooks/useMartyContext';
import Link from 'components/hf/HFLink'; // Need to use dynamic link as MSFT products link externally
import { fetchProductDetail } from 'actions/productDetail';
import { makeAscii } from 'helpers';
import TrustedRetailerBanner from 'components/common/TrustedRetailerBanner';
import MelodyModal from 'components/common/MelodyModal';
import Card from 'components/common/card/Card';
import CardMedia from 'components/common/card/CardMedia';
import CardPrice from 'components/common/card/CardPrice';
import SingleStarRating from 'components/common/SingleStarRating';
import Rating from 'components/Rating';
import SponsoredBanner from 'components/common/SponsoredBanner';
import getCardData from 'data/getCardData';
import useHover from 'hooks/useHover';
import useCrossSite from 'hooks/useCrossSite';
import { toBool } from 'helpers/DataFormatUtils';
import Hearts from 'components/common/Hearts';
import CrossSiteLabel from 'components/common/melodyCard/CrossSiteLabel';

import css from 'styles/components/common/productCard.scss';
import { modal } from 'styles/components/search/crossSiteProducts.scss';

const ProductCard = props => {
  const {
    className,
    brandName,
    intersectionRef,
    isSponsored,
    isCouture,
    isNew,
    onClick,
    productName,
    productSeoUrl,
    productUrl,
    styleColor,
    storeName,
    styleId,
    hydraHeartsStars
  } = props;

  const { testId, marketplace: { search: { showRatingStars, hasStyleRoomFlag } } } = useMartyContext();
  const [ref, setRef] = useState(null);

  const { isHovered } = useHover(ref);
  const { onClick: crossSiteClick, closeClick, content, visible } = useCrossSite(props);
  const { hearts, media, price, reviews } = getCardData({ ...props, isHovered });

  const productLink = productSeoUrl || productUrl || null;
  const encodedBrandName = makeAscii(brandName);
  const encodedProductName = makeAscii(productName);
  const productLabel = `${encodedBrandName} - ${encodedProductName}. Color ${styleColor}.`;

  let flag = null;
  if (isSponsored) {
    flag = <SponsoredBanner newCard={true}/>;
  } else if (hasStyleRoomFlag && toBool(isCouture) && !storeName) {
    flag = <p className={css.styleRoom} data-test-id={testId('styleRoomBanner')}>Style Room</p>;
  } else if (storeName && styleId) {
    flag = <div className={css.isTrusted}><TrustedRetailerBanner styleId={styleId}/></div>;
  } else if (toBool(isNew)) {
    flag = <p className={css.new} data-test-id={testId('newBanner')}>New</p>;
  }

  const modalProps = {
    onRequestClose: () => closeClick(),
    className: modal,
    contentLabel: 'Trusted Retailers',
    isOpen: !!visible
  };

  const makeStars = () => {
    if (showRatingStars) {
      switch (hydraHeartsStars) {
        case 1: return <SingleStarRating {...reviews}/>; // single star - treatment 1
        case 2: return null; // no stars - treatment 2
        case 3: return <Rating // long stars - treatment 3
          rating={reviews.roundedRating}
          reviewCount={reviews.ratingCount}
          hasDisplayReviewCount={true}/>;
        default: // single star - control
          return <SingleStarRating {...reviews}/>;
      }
    }
    return null;
  };

  return (
    <Card
      className={className}
      // For helping site merch collect styleIds https://github01.zappos.net/mweb/marty/pull/9699
      data-style-id={styleId}
      data-test-id={props['data-test-id']}
      itemScope
      itemType="http://schema.org/Product"
      ref={intersectionRef}
    >
      {visible && <MelodyModal {...modalProps}>{content}</MelodyModal>}
      <Link
        className={css.productLink}
        data-style-id={styleId}
        to={productLink}
        itemProp="url"
        data-test-id={testId('searchResultLink')}
        onClick={crossSiteClick || onClick}
        innerRef={setRef}>
        {productLabel} {price.label}. {reviews.label}
      </Link>
      <CardMedia {...media}>
        <div className={css.top}>
          {flag}
          <Hearts {...hearts} />
        </div>
        <div className={css.bottom}>
          {storeName && <div className={css.storeWrapper}><CrossSiteLabel storeName={storeName}/></div>}
        </div>
      </CardMedia>
      <dl className={css.details}>
        {(hydraHeartsStars === 0 || hydraHeartsStars === 1) && makeStars()}
        <dt>Brand Name</dt>
        <dd
          className={css.mainText}
          itemProp="brand"
          itemScope
          itemType="http://schema.org/Brand"
          data-test-id={testId('brand')}>
          {encodedBrandName}
        </dd>
        <dt>Product Name</dt>
        <dd className={css.subText} itemProp="name" data-test-id={testId('productName')}>{encodedProductName}</dd>
        <dt>Color</dt>
        <dd className={css.colorName} itemProp="color" data-test-id={testId('colorName')}>{styleColor}</dd>
        <CardPrice {...price} />
        {hydraHeartsStars === 3 && makeStars()}
      </dl>
    </Card>
  );
};

const mapDispatchToProps = {
  fetchProductDetail
};

const mapStateToProps = state => {
  const { headerFooter: { content }, filters: { term } } = state;
  return {
    term,
    crossSiteModal: content?.Global?.slotData?.vrsnlXsModal || {}
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCard);
